import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { RadioButton } from 'primereact/radiobutton';
import WarrantyPaymentTypeRadio from '../../../../components/common/WarrantyPaymentTypeRadio';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetProformaPartListQuery } from '../../../../api/endpoints/proformaInvoice';

const SEARCH_BY = {
    CODE: 'partCode',
    NAME: 'partName'
};

const ProformaPartDialog = ({ visible, onHide, onSave, editingItem = null }) => {
    const { th, getCurrentLanguageId } = useLanguage();

    const initialForm = {
        itemCode: null,
        itemName: '',
        description: '',
        quantity: 1,
        price: 0,
        totalPrice: 0,
        warrantyType: null,
        type: 1
    };

    const [formData, setFormData] = useState(initialForm);
    const [submitted, setSubmitted] = useState(false);
    const [searchBy, setSearchBy] = useState(SEARCH_BY.NAME);
    const [search, setSearch] = useState({ text: '', input: '', selectedObj: null });
    const debounceRef = useRef(null);

    const { data: partItems = [], isFetching } = useGetProformaPartListQuery(
        {
            languageId: getCurrentLanguageId() || 2,
            ...(searchBy === SEARCH_BY.CODE
                ? { partCode: search.text || undefined }
                : { partName: search.text || undefined }),
            pageSize: 20
        },
        { skip: !visible }
    );

    const partOptions = partItems.map(p => ({
        label: `${p.partCode} — ${p.partName}`,
        value: p.partCode,
        raw: p
    }));

    useEffect(() => {
        if (!visible) {
            setFormData(initialForm);
            setSubmitted(false);
            setSearchBy(SEARCH_BY.NAME);
            setSearch({ text: '', input: '', selectedObj: null });
            return;
        }
        if (editingItem) {
            setFormData({
                id: editingItem.id,
                itemCode: editingItem.itemCode || '',
                itemName: editingItem.itemName || '',
                description: editingItem.description || '',
                quantity: editingItem.quantity ?? 1,
                price: editingItem.price ?? 0,
                totalPrice: editingItem.totalPrice ?? 0,
                warrantyType: editingItem.warrantyType,
                type: editingItem.type ?? 1
            });
        }
    }, [visible, editingItem]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const recalcTotal = (qty, price) => Math.round((qty || 0) * (price || 0) * 100) / 100;

    const handlePartSelect = (val) => {
        if (!val) {
            setSearch(prev => ({ ...prev, selectedObj: null }));
            setFormData(prev => ({ ...prev, itemCode: null, itemName: '', price: 0, totalPrice: 0 }));
            return;
        }
        const opt = partOptions.find(o => o.value === val);
        if (opt?.raw) {
            const p = opt.raw;
            setSearch(prev => ({ ...prev, selectedObj: p }));
            const price = p.unitPrice ?? 0;
            setFormData(prev => ({
                ...prev,
                itemCode: p.partCode,
                itemName: p.partName || '',
                price,
                totalPrice: recalcTotal(prev.quantity, price)
            }));
        }
    };

    const handleFilterInputChange = useCallback((val) => {
        setSearch(prev => ({ ...prev, input: val }));
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearch(prev => ({ ...prev, text: (val ?? '').trim() }));
        }, 400);
    }, []);

    const handleSearchByChange = (value) => {
        if (value === searchBy) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setSearchBy(value);
        setSearch(prev => ({ ...prev, text: '', input: '' }));
    };

    const dropdownFilterTemplate = useCallback(() => (
        <div className="p-dropdown-filter-container" onMouseDown={(e) => e.stopPropagation()}>
            <InputText
                value={search.input}
                onChange={(e) => handleFilterInputChange(e.target.value)}
                className="p-dropdown-filter w-full"
                placeholder={searchBy === SEARCH_BY.CODE ? th('proformaInvoice.searchByPartCode') : th('proformaInvoice.searchByPartName')}
                autoFocus
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            />
            <span className="p-dropdown-filter-icon pi pi-search" />
        </div>
    ), [search.input, searchBy, handleFilterInputChange, th]);

    const handleSave = () => {
        setSubmitted(true);
        if (!formData.itemCode || !formData.quantity || !formData.warrantyType) return;
        onSave({ ...formData, type: 1 });
    };

    const footer = (
        <div className="flex gap-2 justify-content-end w-full">
            <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} outlined />
            <Button
                label={editingItem ? th('common.update') : th('common.create')}
                icon="pi pi-check"
                onClick={handleSave}
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            style={{ width: '40vw' }}
            header={th('proformaInvoice.addPart')}
            modal
            className="p-fluid"
            footer={footer}
            onHide={onHide}
        >
            <div className="field mb-2">
                <label className="font-bold block mb-2">
                    {th('warrantyClaims.detail.partDialog.partCode')} *
                </label>
                <div className="flex flex-wrap gap-4 align-items-center mb-2">
                    <div className="flex align-items-center">
                        <RadioButton
                            inputId="proformaSearchByPartCode"
                            name="proformaPartSearchBy"
                            value={SEARCH_BY.CODE}
                            onChange={(e) => handleSearchByChange(e.value)}
                            checked={searchBy === SEARCH_BY.CODE}
                        />
                        <label htmlFor="proformaSearchByPartCode" className="ml-2 mb-0">
                            {th('proformaInvoice.searchByPartCode')}
                        </label>
                    </div>
                    <div className="flex align-items-center">
                        <RadioButton
                            inputId="proformaSearchByPartName"
                            name="proformaPartSearchBy"
                            value={SEARCH_BY.NAME}
                            onChange={(e) => handleSearchByChange(e.value)}
                            checked={searchBy === SEARCH_BY.NAME}
                        />
                        <label htmlFor="proformaSearchByPartName" className="ml-2 mb-0">
                            {th('proformaInvoice.searchByPartName')}
                        </label>
                    </div>
                </div>
                <Dropdown
                    value={formData.itemCode || null}
                    onChange={(e) => handlePartSelect(e.value)}
                    options={partOptions}
                    optionLabel="label"
                    optionValue="value"
                    placeholder={th('common.itemCodeSelectPlaceholder')}
                    className={`w-full ${submitted && !formData.itemCode ? 'p-invalid' : ''}`}
                    filter
                    filterTemplate={dropdownFilterTemplate}
                    showClear
                    loading={isFetching}
                    emptyMessage={th('common.noRecords')}
                    itemTemplate={(opt) => (
                        <div className="flex align-items-center gap-2">
                            <span className="font-bold">{opt.raw.partCode}</span>
                            <span className="text-color-secondary">—</span>
                            <span>{opt.raw.partName}</span>
                            {!opt.raw.isKarsan && (
                                <span className="ml-auto text-xs px-1 border-round bg-orange-100 text-orange-700">
                                    {th('proformaInvoice.externalPart') || 'Harici'}
                                </span>
                            )}
                        </div>
                    )}
                />
                {submitted && !formData.itemCode && (
                    <small className="p-error">{th('validation.required')}</small>
                )}
            </div>

            <div className="field mb-2">
                <label>{th('warrantyClaims.detail.partDialog.partName')}</label>
                <InputText value={formData.itemName || ''} disabled />
            </div>

            <div className="field mb-2">
                <label>{th('warrantyClaims.detail.partDialog.description')}</label>
                <InputText
                    value={formData.description || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
            </div>

            <div className="grid">
                <div className="col-12 md:col-6">
                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.partDialog.quantity')} *</label>
                        <InputNumber
                            value={formData.quantity}
                            onChange={(e) => {
                                const qty = e.value || 0;
                                setFormData(prev => ({
                                    ...prev,
                                    quantity: qty,
                                    totalPrice: recalcTotal(qty, prev.price)
                                }));
                            }}
                            min={0}
                            className={submitted && !formData.quantity ? 'p-invalid' : ''}
                        />
                        {submitted && !formData.quantity && (
                            <small className="p-error">{th('validation.required')}</small>
                        )}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.partDialog.partPrice')}</label>
                        <InputNumber
                            value={formData.price}
                            mode="decimal"
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="field mb-2">
                <label>{th('proformaInvoice.totalPrice')}</label>
                <InputNumber
                    value={formData.totalPrice ?? 0}
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    disabled
                />
            </div>

            <div className="field mb-2">
                <label className="font-bold block mb-2">
                    {th('warrantyClaims.detail.partDialog.warrantyType')} *
                </label>
                <WarrantyPaymentTypeRadio
                    value={formData.warrantyType}
                    onChange={(val) => setFormData(prev => ({ ...prev, warrantyType: val }))}
                    className={submitted && !formData.warrantyType ? 'p-invalid' : ''}
                    excludeOptions={['3']}
                />
                {submitted && !formData.warrantyType && (
                    <small className="p-error">{th('validation.required')}</small>
                )}
            </div>
        </Dialog>
    );
};

export default ProformaPartDialog;
